import AdminLayout from "../../layouts/AdminLayout";
import EngineerLayout from "../../layouts/EngineerLayout";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ClientLayout from "../../layouts/ClientLayout";
import API from "../../services/api";
import {
  MessageSquare, Star, Calendar, CheckCircle2,
  PenLine, HardHat, Clock, Handshake, Link2, Send,
  ArrowLeft, XCircle, X,
} from "lucide-react";

/* ─── Roboto font ──────────────────────────────────────────────────────────── */
const ROBOTO = `
 @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');
  .ta-root, .ta-root * { font-family: 'Montserrat', sans-serif !important; }

  .ta-textarea:focus { border-color: #1a56db !important; box-shadow: 0 0 0 4px rgba(26,86,219,0.10) !important; }
  .ta-btn-primary { transition: box-shadow 0.18s, transform 0.14s, background 0.18s; }
  .ta-btn-primary:hover { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(26,86,219,0.38) !important; }
  .ta-btn-primary:active { transform: translateY(0); }
  .ta-btn-dash { transition: background 0.15s, color 0.15s, box-shadow 0.15s; }
  .ta-btn-dash:hover { background: #1e3a6e !important; box-shadow: 0 4px 14px rgba(30,58,110,0.25) !important; }
  .ta-card-feedback { transition: transform 0.18s, box-shadow 0.18s; }
  .ta-card-feedback:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(26,86,219,0.10) !important; }
  .ta-char-ok { color: #6b7280; }
  .ta-char-warn { color: #d97706; }
  .ta-char-over { color: #dc2626; }
  .ta-back-btn { transition: all 0.18s; }
  .ta-back-btn:hover { border-color: #1a56db !important; background: #EEF2FF !important; }
  .ta-stat-pill { transition: transform 0.15s; }
  .ta-stat-pill:hover { transform: translateY(-2px); }

  @keyframes ta-toast-in { from { opacity:0; transform:translateY(12px) scale(0.97); } to { opacity:1; transform:none; } }

  @media (max-width: 768px) {
    .ta-hero-inner { flex-direction: column !important; gap: 24px !important; }
    .ta-hero-stats { justify-content: center !important; }
    .ta-page-header { flex-direction: column !important; gap: 12px !important; align-items: flex-start !important; }
    .ta-feedback-grid { grid-template-columns: 1fr !important; }
    .ta-summary-strip { grid-template-columns: 1fr 1fr !important; }
  }
`;

/* ─── Colour tokens ────────────────────────────────────────────────────────── */
const C = {
  navy:        "#0F2A4A",
  navyMid:     "#1e3a6e",
  blue:        "#1a56db",
  blueMid:     "#3b71ca",
  blueLight:   "#EEF2FF",
  teal:        "#0891b2",
  tealLight:   "#F0FDFA",
  gold:        "#d97706",
  goldLight:   "#FFFBEB",
  green:       "#059669",
  greenLight:  "#ECFDF5",
  surface:     "#FFFFFF",
  bg:          "#F1F5FB",
  border:      "#DFE8F5",
  dark:        "#0F172A",
  body:        "#374151",
  muted:       "#6B7280",
  faint:       "#9CA3AF",
};

const MAX_CHARS = 1000;

/* ─── Toast styles keyed by type ─────────────────────────────────────────── */
const TOAST_STYLES = {
  success: { bg: "#059669", icon: <CheckCircle2 size={16} /> },
  error:   { bg: "#dc2626", icon: <XCircle size={16} /> },
};

/* ═══════════════════════════════════════════════════════════════════════════
   COMPONENT — all state, effects, handlers, API calls UNCHANGED
   (except alert() → toast, and handleSave now verifies against the server
   before reporting failure — see comments below)
═══════════════════════════════════════════════════════════════════════════ */
function Testimonials({ layout }) {
  const [projects,     setProjects]     = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [engineers,    setEngineers]    = useState([]);
  const navigate = useNavigate();

 const role   = localStorage.getItem("role")?.toLowerCase();
const Layout = layout === "engineer" ? EngineerLayout : ClientLayout;
const { id: projectId } = useParams();

const [formData, setFormData] = useState({
    project_id:  "",
    engineer_id: "",
    feedback:    "",
    rating:      0,
  });

  const [selectedCategory, setSelectedCategory] = useState("");

  // NEW — toast state, replaces alert()
  const [toast, setToast] = useState(null); // { message, type: "success" | "error" }

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRatingChange = (value) => {
    setFormData({ ...formData, rating: value });
  };
  const handleBackToDashboard = () => {
    if (role === "client") { navigate("/client-dashboard"); }
    else                   { navigate("/admin-dashboard");  }
  };
  const handleBackToProject = () => {
    navigate(`/engineer/project/${projectId}`);
  };

  /* ── UNCHANGED effects ── */
 useEffect(() => {
  const request = layout === "engineer" && projectId
    ? API.get(`/testimonials/project/${projectId}`)
    : API.get("/testimonials");

  request
    .then((res) => { setTestimonials(res.data.data || []); })
    .catch((err) => { console.error(err); });
}, [layout, projectId]);

  /* ── UPDATED save — alert() replaced with toast, and now verifies against
     the server before reporting failure: the write can succeed on the
     backend even when the HTTP response back to the browser fails (an
     infra-level dropped-response issue seen on the live domain, not a bug
     in this save logic), so we double-check instead of assuming failure. ── */
const handleSave = async () => {
if (!formData.feedback || !formData.rating) {
  setToast({ message: "Please fill all fields and select a rating", type: "error" });
  return;
}
if (!selectedCategory) {
  setToast({ message: "Please select a category — Site Quality, Timeliness, or Communication.", type: "error" });
  return;
}
    const feedbackToSave = formData.feedback;
    try {
const response = await API.post("/testimonials", {
  project_id:  localStorage.getItem("project_id"),
  feedback:    feedbackToSave,
  rating:      formData.rating,
  user_id:     localStorage.getItem("id"),
  client_name: localStorage.getItem("name") || "Client",
  category:    selectedCategory,
});
      if (response.data.success) {
        setToast({ message: "Feedback Saved Successfully", type: "success" });
        const res = await API.get("/testimonials");
        setTestimonials(res.data.data || []);
        setFormData({ feedback: "", rating: 0 });
setSelectedCategory("");
      }
    } catch (error) {
      console.error(error);
      // The request may have actually written to the DB even though the
      // response back to the browser failed — check before telling the
      // user it didn't save.
      try {
        const res = await API.get("/testimonials");
        const list = res.data.data || [];
        const justSaved = list.some(
          (t) => (t.feedback || t.comment) === feedbackToSave && (t.rating || 0) === formData.rating
        );
        setTestimonials(list);
        if (justSaved) {
          setToast({ message: "Feedback Saved Successfully", type: "success" });
          setFormData({ feedback: "", rating: 0 });
setSelectedCategory("");
          return;
        }
      } catch (verifyErr) {
        console.error("Verification check also failed:", verifyErr);
      }
      setToast({ message: "Error saving testimonial", type: "error" });
    }
  };

  /* ── derived ── */
  const charCount      = formData.feedback.length;
  const charClass      = charCount > MAX_CHARS ? "ta-char-over" : charCount > MAX_CHARS * 0.85 ? "ta-char-warn" : "ta-char-ok";
const submitDisabled = !formData.feedback.trim() || charCount > MAX_CHARS || !formData.rating;
  const clientName     = localStorage.getItem("name") || "Valued Client";
  const projectRef     = localStorage.getItem("project_id");

  /* ── NEW: display-only derived stats for the engineer summary strip ── */
  const avgRating = testimonials.length > 0
    ? (testimonials.reduce((sum, t) => sum + (t.rating || 0), 0) / testimonials.length)
    : 0;
  const latestFeedbackDate = testimonials.length > 0
    ? testimonials
        .map(t => t.created_at)
        .filter(Boolean)
        .sort((a, b) => new Date(b) - new Date(a))[0]
    : null;

  /* ════════════════════════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════════════════════════ */
  return (
    <Layout>
      <style>{ROBOTO}</style>

      {/* NEW — toast, replaces alert() */}
      {toast && (
        <div style={{
          position: "fixed", top: 24, right: 24, zIndex: 99999,
          background: TOAST_STYLES[toast.type]?.bg || TOAST_STYLES.success.bg,
          color: "#fff", padding: "14px 18px", borderRadius: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          display: "flex", alignItems: "center", gap: 12,
          minWidth: 280, maxWidth: 380,
          animation: "ta-toast-in 0.22s ease",
          fontFamily: "'Montserrat', sans-serif",
        }}>
          <span style={{ display: "inline-flex", flexShrink: 0 }}>
            {TOAST_STYLES[toast.type]?.icon || <CheckCircle2 size={16} />}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{toast.message}</div>
          </div>
          <button onClick={() => setToast(null)} style={{
            background: "rgba(255,255,255,0.2)", border: "none", color: "#fff",
            width: 24, height: 24, borderRadius: "50%", cursor: "pointer",
            fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}><X size={12} /></button>
        </div>
      )}

      <div className="ta-root" style={{ background: C.bg, minHeight: "100vh", padding: "0 0 60px" }}>
{/* ── BACK BUTTON ── */}
        {layout === "engineer" && (
          <div style={{ maxWidth: 1080, margin: "0 auto", padding: "16px 32px 16px" }}>
            <button
              className="ta-back-btn"
              onClick={handleBackToProject}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "#fff",
                border: "1.5px solid #E2E8F0",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                fontWeight: 600,
                color: C.blue,
                cursor: "pointer",
                fontFamily: "'Montserrat', sans-serif",
              }}
            >
              <ArrowLeft size={14} /> Back to Project
            </button>
          </div>
        )}

        {/* ── PAGE HEADER ──────────────────────────────────────────────────── */}
        <div style={{
          background: C.surface,
          borderBottom: `1px solid ${C.border}`,
          padding: "20px 32px",
          marginBottom: 0,
        }}>
          <div className="ta-page-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", maxWidth: 1080, margin: "0 auto" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {/* Icon */}
              <div style={{
                width: 44, height: 44, borderRadius: 12,
                background: `linear-gradient(135deg,${C.blue},${C.blueMid})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#fff", boxShadow: "0 4px 14px rgba(26,86,219,0.28)", flexShrink: 0,
              }}><MessageSquare size={22} /></div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <h1 style={{ fontSize: 20, fontWeight: 800, color: C.dark, margin: 0, letterSpacing: "-0.3px" }}>
                    Client Feedback
                  </h1>
                  <span style={{
                    fontSize: 9, fontWeight: 800, color: C.blue,
                    background: C.blueLight, border: `1px solid #C7D2FE`,
                    padding: "2px 8px", borderRadius: 20, letterSpacing: "0.08em", textTransform: "uppercase",
                  }}>Total Assure</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>
                  {layout === "engineer"
                    ? "See what the client has shared about this project"
                    : "Tell us how the project is going"}
                </div>
              </div>
            </div>
          </div>
        </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 32px 0" }}>

  {layout === "engineer" ? (
    /* ── ENGINEER VIEW — read only feedback list ── */
    <div>

      {/* ── NEW: summary strip ── */}
      <div className="ta-summary-strip" style={{
        display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 26,
      }}>
        <div className="ta-stat-pill" style={{
          background: C.surface, borderRadius: 14, border: `1.5px solid ${C.border}`,
          padding: "16px 18px", display: "flex", alignItems: "center", gap: 12,
          boxShadow: "0 2px 10px rgba(26,86,219,0.05)",
        }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.blueLight, display: "flex", alignItems: "center", justifyContent: "center", color: C.blue }}><MessageSquare size={17} /></div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Feedback</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.dark, lineHeight: 1.2 }}>{testimonials.length}</div>
          </div>
        </div>
        <div className="ta-stat-pill" style={{
          background: C.surface, borderRadius: 14, border: `1.5px solid ${C.border}`,
          padding: "16px 18px", display: "flex", alignItems: "center", gap: 12,
          boxShadow: "0 2px 10px rgba(26,86,219,0.05)",
        }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.goldLight, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold }}><Star size={17} fill={C.gold} /></div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Average Rating</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.dark, lineHeight: 1.2 }}>
              {testimonials.length > 0 ? avgRating.toFixed(1) : "—"}
            </div>
          </div>
        </div>
        <div className="ta-stat-pill" style={{
          background: C.surface, borderRadius: 14, border: `1.5px solid ${C.border}`,
          padding: "16px 18px", display: "flex", alignItems: "center", gap: 12,
          boxShadow: "0 2px 10px rgba(26,86,219,0.05)",
        }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: C.tealLight, display: "flex", alignItems: "center", justifyContent: "center", color: C.teal }}><Calendar size={17} /></div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>Most Recent</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.dark, lineHeight: 1.3 }}>
              {latestFeedbackDate
                ? new Date(latestFeedbackDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
                : "—"}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 3, height: 22, borderRadius: 4, background: `linear-gradient(180deg,${C.blue},${C.teal})` }} />
        <div>
          <div style={{ fontWeight: 800, fontSize: 16, color: C.dark }}>Client Feedback</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
            {testimonials.length} response{testimonials.length !== 1 ? "s" : ""} for this project
          </div>
        </div>
      </div>

      {testimonials.length === 0 ? (
        <div style={{
          background: C.surface, borderRadius: 18,
          border: `1.5px dashed ${C.border}`,
          padding: "56px 40px", textAlign: "center",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: "50%",
            background: C.blueLight, border: `1.5px solid #C7D2FE`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: C.blue, marginBottom: 6,
          }}><MessageSquare size={28} /></div>
          <div style={{ fontWeight: 700, fontSize: 16, color: C.dark }}>No feedback yet</div>
          <div style={{ fontSize: 13, color: C.muted, maxWidth: 320, lineHeight: 1.6 }}>
            The client hasn't submitted any feedback for this project yet. Once they do, it will show up here.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 16 }}>
          {testimonials.map((item, idx) => (
            <div key={item.id || idx} className="ta-card-feedback" style={{
              background: C.surface, borderRadius: 16,
              border: `1.5px solid ${C.border}`,
              padding: "22px 24px",
              boxShadow: "0 2px 10px rgba(26,86,219,0.05)",
              position: "relative", overflow: "hidden",
            }}>
              <div style={{
                position: "absolute", top: 14, right: 18,
                fontSize: 48, color: C.blueLight, lineHeight: 1,
                fontWeight: 900, fontFamily: "Georgia, serif", pointerEvents: "none",
              }}>"</div>
              <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: `linear-gradient(135deg,${C.blue},${C.teal})`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, color: "#fff", fontWeight: 800, flexShrink: 0,
                }}>C</div>
<div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.dark }}>{item.client_name || "Client"}</div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>Project #{item.project_id}</div>
                </div>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: C.greenLight, border: "1px solid #A7F3D0",
                  borderRadius: 20, padding: "3px 9px",
                  fontSize: 9, fontWeight: 700, color: C.green,
                }}><CheckCircle2 size={11} /> Verified</div>
              </div>
              <p style={{ margin: "0 0 16px", fontSize: 13, color: C.body, lineHeight: 1.75 }}>
                {item.comment || item.feedback}
              </p>
              <div style={{ paddingTop: 12, borderTop: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                {item.created_at ? (
                  <div style={{ fontSize: 10, color: C.faint, display: "flex", alignItems: "center", gap: 4 }}>
                    <Calendar size={11} /> {new Date(item.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                  </div>
                ) : <div />}
<div style={{ display: "flex", gap: 2 }}>
                  {[1,2,3,4,5].map(s => (
                    <Star
                      key={s}
                      size={12}
                      fill={s <= (item.rating || 0) ? "#F59E0B" : "#E5E7EB"}
                      color={s <= (item.rating || 0) ? "#F59E0B" : "#E5E7EB"}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : (
    /* ── CLIENT VIEW — feedback form (unchanged) ── */
    <div style={{
      background: C.surface, borderRadius: 18,
      border: `1.5px solid ${C.border}`,
      boxShadow: "0 4px 24px rgba(26,86,219,0.07)",
      overflow: "hidden", marginBottom: 28,
    }}>
      <div style={{ height: 4, background: `linear-gradient(90deg,${C.blue},${C.teal},#34D399)` }} />
      <div style={{ padding: "36px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 11,
            background: C.blueLight, border: `1px solid #C7D2FE`,
            display: "flex", alignItems: "center", justifyContent: "center", color: C.blue,
          }}><PenLine size={19} /></div>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, color: C.dark }}>Share Your Experience</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
              Hello, <strong style={{ color: C.dark }}>{clientName}</strong> — your project feedback matters to us.
            </div>
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 24 }}>
          {[
            { icon: <HardHat size={18} />, label: "Site Quality",  q: "How was the workmanship on-site?" },
            { icon: <Clock size={18} />, label: "Timeliness",    q: "Did we meet agreed timelines?" },
            { icon: <Handshake size={18} />, label: "Communication", q: "How responsive was the team?" },
          ].map(p => (
            <div key={p.label}
  onClick={() => setSelectedCategory(p.label)}
  style={{ 
    borderRadius: 12, padding: "14px 16px", 
    background: selectedCategory === p.label ? "#EEF2FF" : C.bg, 
    border: `1.5px solid ${selectedCategory === p.label ? C.blue : C.border}`,
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: selectedCategory === p.label ? "0 4px 12px rgba(26,86,219,0.15)" : "none"
  }}>
              <div style={{ color: C.blue, marginBottom: 6 }}>{p.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.dark, marginBottom: 3 }}>{p.label}</div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{p.q}</div>
            </div>
          ))}
        </div>
        <div style={{ position: "relative", marginBottom: 10 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Your Feedback <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <textarea
            className="ta-textarea"
            rows="7"
            name="feedback"
            placeholder="Describe your experience..."
            value={formData.feedback}
            onChange={handleChange}
            maxLength={MAX_CHARS + 50}
            style={{
              width: "100%", borderRadius: 12, border: `1.5px solid ${C.border}`,
              padding: "16px 18px", fontSize: 14, color: C.dark,
              background: "#FAFCFF", outline: "none", resize: "vertical",
             lineHeight: 1.75, boxSizing: "border-box", fontFamily: "Montserrat, sans-serif",
            }}
          />
<div style={{ display: "flex", justifyContent: "space-between", marginTop: 7 }}>
            <div style={{ fontSize: 11, color: C.muted }}>Be specific — detailed feedback leads to meaningful improvements.</div>
            <div className={charClass} style={{ fontSize: 11, fontWeight: 700 }}>{charCount} / {MAX_CHARS}</div>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Your Rating <span style={{ color: "#dc2626" }}>*</span>
          </label>
          <div style={{ display: "flex", gap: 6 }}>
            {[1, 2, 3, 4, 5].map((s) => (
              <Star
                key={s}
                size={28}
                onClick={() => handleRatingChange(s)}
                style={{ cursor: "pointer" }}
                fill={s <= formData.rating ? "#F59E0B" : "#E5E7EB"}
                color={s <= formData.rating ? "#F59E0B" : "#E5E7EB"}
              />
            ))}
          </div>
        </div>

        {/* {projectRef && (
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: C.blueLight, border: `1px solid #C7D2FE`,
            borderRadius: 8, padding: "6px 12px", fontSize: 11,
            fontWeight: 600, color: C.blue, marginBottom: 24,
          }}>
            <Link2 size={13} /> Linked to project <strong>#{projectRef}</strong>
          </div>
        )} */}
        <button
          className="ta-btn-primary"
          onClick={handleSave}
          disabled={submitDisabled}
          style={{
            width: "100%",
            background: submitDisabled ? "#D1D5DB" : `linear-gradient(135deg,${C.blue},${C.blueMid})`,
            color: submitDisabled ? "#9CA3AF" : "#fff",
            border: "none", padding: "16px 0", borderRadius: 12,
            fontSize: 15, fontWeight: 800, cursor: submitDisabled ? "not-allowed" : "pointer",
            boxShadow: submitDisabled ? "none" : "0 6px 20px rgba(26,86,219,0.32)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
          }}
        >
          <Send size={18} />
          Submit Feedback
        </button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 14, fontSize: 11, color: C.faint }}>
          Your feedback is confidential and used only for service improvement.
        </div>
      </div>
    </div>
  )}

</div>
      </div>
    </Layout>
  );
}

export default Testimonials;